import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { formatDate } from "../lib/format";
import { Loader2, Printer, FileText } from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { sediList, printLetterHtml } from "../lib/letterPdf";
import { LetterA4Preview } from "./LetterA4Preview";

// Preset: templateId (da Lettere) oppure praticaId (da Pratiche)
export default function GenerateLetterDialog({ open, onOpenChange, presetTemplateId, presetPraticaId }) {
  const [templates, setTemplates] = useState([]);
  const [pratiche, setPratiche] = useState([]);
  const [templateId, setTemplateId] = useState(presetTemplateId || "");
  const [praticaId, setPraticaId] = useState(presetPraticaId || "");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [withHead, setWithHead] = useState("con");
  const [studio, setStudio] = useState(null);
  const inte = studio ? sediList(studio)[0] : null;
  const margins = studio?.settings?.print_margins;

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setTemplateId(presetTemplateId || "");
    setPraticaId(presetPraticaId || "");
    api.get("/studio/me").then((r) => setStudio(r.data)).catch(() => {});
    Promise.all([api.get("/studio/letter-templates"), api.get("/studio/pratiche")])
      .then(([t, p]) => { setTemplates(t.data); setPratiche(p.data); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [open, presetTemplateId, presetPraticaId]);

  const generate = async () => {
    if (!templateId || !praticaId) {
      toast.error("Seleziona template e pratica");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post(`/studio/letter-templates/${templateId}/generate`, { pratica_id: praticaId });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="generate-letter-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><FileText className="h-5 w-5 text-amber-400" /> Genera lettera</DialogTitle>
          <DialogDescription className="text-slate-400">
            Scegli template e pratica: le variabili verranno sostituite con i dati reali.
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300">Template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="gen-template-select" className="mt-1.5 bg-slate-900/60 border-slate-700"><SelectValue placeholder="Seleziona template" /></SelectTrigger>
              <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Pratica</Label>
            <Select value={praticaId} onValueChange={setPraticaId}>
              <SelectTrigger data-testid="gen-pratica-select" className="mt-1.5 bg-slate-900/60 border-slate-700"><SelectValue placeholder="Seleziona pratica" /></SelectTrigger>
              <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                {pratiche.map((p) => <SelectItem key={p.id} value={p.id}>{p.numero_pratica} · {p.nome_assistito}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button onClick={generate} disabled={generating} data-testid="gen-run-button"
          className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold w-full">
          {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Compila documento"}
        </Button>

        {result && (
          <div className="mt-2 space-y-3" data-testid="gen-preview">
            <div className="flex items-center gap-3">
              <Label className="text-slate-300 text-sm">Intestazione</Label>
              <Select value={withHead} onValueChange={setWithHead}>
                <SelectTrigger data-testid="gen-letterhead-select" className="w-56 bg-slate-900/60 border-slate-700 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                  <SelectItem value="con">Con carta intestata</SelectItem>
                  <SelectItem value="senza">Senza carta intestata</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => printLetterHtml(result.title, result.content, withHead === "con", inte, margins)}
                data-testid="gen-print-button" className="ml-auto bg-cyan-600 hover:bg-cyan-700 text-white">
                <Printer className="h-4 w-4 mr-1" /> Stampa
              </Button>
            </div>
            <div className="rounded-lg bg-slate-200 p-3 max-h-[40vh] overflow-y-auto" data-testid="gen-preview-body">
              <LetterA4Preview contentHtml={result.content} withHead={withHead === "con"} inte={inte} margins={margins} center />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export { formatDate };
